<script setup lang="ts">

import { ref, toRef, watch, onBeforeUnmount } from 'Vue';
import { FocusTrap } from 'focus-trap-vue'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import type { BodyScrollOptions } from 'body-scroll-lock';
import Swiper from 'swiper'

const activeFocusTrap = ref(false)
const overlayElement = ref(null)

const props = defineProps<{
	show: Boolean;
	choice: PartChoice;
	onClick: (choice: PartChoice) => void;
}>();

const show = toRef(props, 'show');

interface SwiperHTMLElement extends HTMLElement {
  swiper: Swiper;
}

const swiperRef = ref<SwiperHTMLElement|null>(null);

const swiperPrev = () => {
	swiperRef.value?.swiper.slidePrev();
}

const swiperNext = () => {
	swiperRef.value?.swiper.slideNext();
}

const enableFocusTrap = () => {
  setTimeout(function () {
    activeFocusTrap.value = true
  }, 50)
}

const disableFocusTrap = () => {
  activeFocusTrap.value = false
}

const bodyScrollOptions: BodyScrollOptions = {
  reserveScrollBarGap: true,
};

const open = function () {
  disableBodyScroll(overlayElement, bodyScrollOptions)
  enableFocusTrap()
}

const close = function () {
  disableFocusTrap()
  enableBodyScroll(overlayElement, bodyScrollOptions)
}

watch(show, (newShow, oldShow) => {
  if (newShow) {
	open()
  } else {
	close()
  }
})

onBeforeUnmount(() => close())

</script>

<template>
	<Transition name="modal">
		<div
			v-if="show"
			class="modal__mask"
			role="dialog"
			aria-label="Produktinfo"
			aria-modal="true"
			:aria-hidden="!show"
		>
			<FocusTrap v-model:active="activeFocusTrap" :initial-focus="() => $refs.closeButton">
				<div class="modal__container">
					<button
						class="modal__close-button"
						@click="$emit('close')"
						ref="closeButton"
						aria-label="Lukk produktinfo"
					>Lukk vindu<svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M11.5564 9.49999L17.8272 3.24374C18.1018 2.96913 18.2561 2.59668 18.2561 2.20832C18.2561 1.81996 18.1018 1.44751 17.8272 1.1729C17.5526 0.898293 17.1802 0.744019 16.7918 0.744019C16.4035 0.744019 16.031 0.898293 15.7564 1.1729L9.50015 7.44374L3.24391 1.1729C2.9693 0.898293 2.59684 0.744019 2.20849 0.744019C1.82013 0.744019 1.44768 0.898293 1.17307 1.1729C0.898462 1.44751 0.744188 1.81996 0.744188 2.20832C0.744188 2.59668 0.898462 2.96913 1.17307 3.24374L7.4439 9.49999L1.17307 15.7562C1.03638 15.8918 0.927893 16.0531 0.853855 16.2308C0.779818 16.4085 0.741699 16.5991 0.741699 16.7917C0.741699 16.9842 0.779818 17.1748 0.853855 17.3525C0.927893 17.5302 1.03638 17.6915 1.17307 17.8271C1.30864 17.9638 1.46994 18.0722 1.64765 18.1463C1.82536 18.2203 2.01597 18.2584 2.20849 18.2584C2.40101 18.2584 2.59162 18.2203 2.76933 18.1463C2.94704 18.0722 3.10833 17.9638 3.24391 17.8271L9.50015 11.5562L15.7564 17.8271C15.892 17.9638 16.0533 18.0722 16.231 18.1463C16.4087 18.2203 16.5993 18.2584 16.7918 18.2584C16.9843 18.2584 17.175 18.2203 17.3527 18.1463C17.5304 18.0722 17.6917 17.9638 17.8272 17.8271C17.9639 17.6915 18.0724 17.5302 18.1465 17.3525C18.2205 17.1748 18.2586 16.9842 18.2586 16.7917C18.2586 16.5991 18.2205 16.4085 18.1465 16.2308C18.0724 16.0531 17.9639 15.8918 17.8272 15.7562L11.5564 9.49999Z" fill="black"/>
					</svg></button>

					<div v-if="!!choice?.gallery?.length && choice.gallery.length > 1" class="modal__figure modal__figure--square modal__figure--swiper">
						<swiper-container slides-per-view="1" speed="500" loop="true" css-mode="false" class="modal__swiper" ref="swiperRef">
							<swiper-slide class="modal__swiper-slide" v-for="(image, index) in choice.gallery" :key="index"><img :src="image.url" :width="image.width" :height="image.height" loading="lazy" :alt="image?.alt" class="modal__swiper-img" /></swiper-slide>
						</swiper-container>
						<button @click="swiperPrev()" class="modal__swiper-button modal__swiper-button--prev" type="button" aria-label="Forrige"></button>
						<button @click="swiperNext()" class="modal__swiper-button modal__swiper-button--next" type="button" aria-label="Neste"></button>
					</div>

					<div class="modal__figure modal__figure--square modal__figure--img" v-else-if="choice?.img.url">
						<img :src="choice.img.url" :width="choice.img.width" :height="choice.img.height" loading="lazy" :alt="choice.prodNo" class="modal__img" />
					</div>

					<div class="modal__body">
						<div class="modal__content">
							<h2 class="modal__description" v-html="choice.description"></h2>
							<div class="modal__prod-no">Produktnummer: {{choice.prodNo}}</div>
							<div class="modal__details">
								<ul>
									<li v-for="(detail, index) in choice.details" :key="index">{{detail.title}}: {{detail.value ? detail.value : '—'}}</li>
								</ul>
							</div>
							<div class="modal__foot">
								<button @click="onClick(choice)" type="button" class="button">Velg produkt</button>
							</div>
						</div>
					</div>
				</div>
			</FocusTrap>
		</div>
	</Transition>
</template>

<style lang="scss" scoped>
.modal {
	$block: '.modal';

	&__mask {
		position: fixed;
		z-index: 9998;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.5);
		transition: opacity 0.3s ease;
		overflow: auto;
		padding-top: 20px;
		padding-bottom: 20px;
		display: grid;
		align-items: center;
	}

	&__container {
		margin: 0px auto;
		padding: 25px;
		background-color: #ffffff;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
		transition: all 0.3s ease;
		max-width: 1300px;
		width: calc(100% - 40px);
		display: flex;
		justify-content: space-between;
		position: relative;
		gap: 20px;
		flex-wrap: wrap;
		flex-direction: column;
		align-items: flex-end;

		@media (min-width: 768px) {
			flex-direction: row;
			align-items: flex-start;
		}
	}

	&__figure {
		position: relative;
		width: 100%;
		background: transparent;

		@media (min-width: 768px) {
			width: calc(50% - 10px);
		}

		&--square {
			&::after {
				content: "";
				padding-bottom: 100%;
				width: 100%;
				display: block;
			}

			#{$block} {
				&__img,
				&__swiper {
					position: absolute;
					top: 0;
					right: 0;
					bottom: 0;
					left: 0;
					width: 100%;
					height: 100%;
					max-width: 100%;
					max-height: 100%;
				}

				&__img {
					object-fit: contain;
				}
			}
		}
	}

	&__swiper-slide {
		display: flex;
		justify-content: center;
		align-items: center;
	}

	&__swiper-button {
		position: absolute;
		top: 50%;
		z-index: 1;
		background-color: #55806b;
		color: #fff;
		border-radius: 50%;
		width: 50px;
		height: 50px;
		display: flex;
		justify-content: center;
		align-items: center;

		&--prev,
		&--next {
			&::after {
				content:"";
				width: 16px;
				height: 16px;
				background-size: 16px 16px;
				background-repeat: no-repeat;
				display: block;
			}
		}

		&--prev {
			left: 10px;

			&::after {
				background-image: url('./../../svg/ikoner_navigasjon_arrow_left.svg');
			}
		}

		&--next {
			right: 10px;

			&::after {
				background-image: url('./../../svg/ikoner_navigasjon_arrow_right.svg');
			}
		}
	}

	&__body {
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;

		@media (min-width: 768px) {
			width: calc(50% - 10px);
			padding-top: 70px;
			padding-bottom: 70px;
		}
	}

	&__content {
		width: 100%;
		max-width: 500px;
		width: 100%;

		@media (min-width: 768px) {
			margin-left: 50px;
		}
	}

	&__prod-no {
		margin-bottom: 1rem;
	}

	&__description {
		font-size: 1.5625rem;
		font-weight: 400;
		font-weight: 400;
		line-height: 1.2;

		:deep(p) {
			margin-top: 0;
			margin-bottom: 0;
			line-height: 1.4;
		}

		:deep(p:not(:last-child)) {
			margin-bottom: 0.5rem;
		}
	}

	&__details {
		margin-bottom: 40px;

		ul {
			list-style: disc;
			padding-left: 1.7rem;
		}

		li {
			margin-bottom: 0.1rem;
		}
	}

	&__foot {
		.button {
			display: inline-block;
		}
	}

	&__head {
		padding: 17px;
		text-align: right;
	}

	&__close-button {
		@media (min-width: 768px) {
			position: absolute;
			top: 37px;
			right: 37px;
		}

		svg {
			display: inline-block;
			margin-left: 22px;
		}
	}
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal__container,
.modal-leave-to .modal__container {
  -webkit-transform: scale(0.9);
  transform: scale(0.9);
}
</style>
